/**
 * Parses a GPX XML string into a structured JavaScript object.
 * Relies on the browser's DOMParser.
 * NOTE: Uses non-namespaced methods (getElementsByTagName, querySelector) for
 * core GPX tags (trk, wpt, trkpt, ele, time etc.) as this proved more reliable
 * in the specific target environment than stricter getElementsByTagNameNS, likely
 * due to browser parser leniency. Namespaced methods ARE used for known extension data.
 * Includes enhanced error checking.
 *
 * @param {string} xmlString The raw GPX XML content as a string.
 * @returns {{metadata: object, waypoints: Array, bounds: object|null, tracks: Array}} The parsed data object, or an empty structure on error.
 */

// --- Namespace Constants ---
const GARMIN_TPX_NS = "http://www.garmin.com/xmlschemas/TrackPointExtension/v1";
// Add other known extension namespaces here if needed
// const GARMIN_GPXX_NS = "http://www.garmin.com/xmlschemas/GpxExtensions/v3";

// --- Safe Helper Functions ---
const safeGetTextContent = (node, tagName) => {
  // Uses non-namespaced as per the working strategy for core tags
  if (!node) return null;
  const element = node.getElementsByTagName(tagName)[0];
  return element ? element.textContent?.trim() || null : null; // Trim whitespace
};
const safeGetAttribute = (node, attributeName) => {
  return node ? node.getAttribute(attributeName) : null;
};
const safeParseFloat = (text) => {
  if (text === null || text === undefined || text === "") return null;
  const number = parseFloat(text);
  return isNaN(number) ? null : number;
};
const safeParseInt = (text) => {
  if (text === null || text === undefined || text === "") return null;
  const number = parseInt(text, 10);
  return isNaN(number) ? null : number;
};

// --- Main Parsing Function ---
export const parseGPX = (xmlString) => {
  if (typeof xmlString !== "string" || !xmlString.trim().startsWith("<")) {
    console.error("Input to parseGPX is not a valid XML string!");
    return { metadata: {}, waypoints: [], bounds: null, tracks: [] };
  }

  const parser = new DOMParser();
  let xmlDoc;
  try {
    xmlDoc = parser.parseFromString(xmlString, "application/xml");
  } catch (parseError) {
    console.error("DOMParser failed:", parseError);
    return { metadata: {}, waypoints: [], bounds: null, tracks: [] };
  }

  const parserErrorNode = xmlDoc.querySelector("parsererror");
  if (parserErrorNode) {
    console.warn("GPX XML Parsing Warning:", parserErrorNode.textContent);
    console.warn("Faulty Input String (start):", xmlString.substring(0, 500));
    // Allow proceeding despite potential minor errors
  }

  try {
    // Prioritize bounds within metadata if it exists there
    const metadata = getMetadata(xmlDoc);
    const bounds = metadata.bounds || getBounds(xmlDoc); // Use metadata bounds if found, else check root

    const waypoints = getWaypoints(xmlDoc);
    const tracks = getTracks(xmlDoc);
    const routes = getRoutes(xmlDoc);
    console.log("gpx parser", xmlDoc);

    return {
      metadata,
      waypoints,
      bounds,
      tracks,
      routes,
    };
  } catch (extractionError) {
    console.error("Error during GPX data extraction:", extractionError);
    return { metadata: {}, waypoints: [], bounds: null, tracks: [] };
  }
};

// --- Extraction Helpers ---

const getMetadata = (xmlDoc) => {
  const metadata = {};
  const metadataNode = xmlDoc.getElementsByTagName("metadata")[0]; // Non-NS
  if (!metadataNode) return metadata;

  // Basic Info
  metadata.name = safeGetTextContent(metadataNode, "name"); // Get top-level name if present
  metadata.desc = safeGetTextContent(metadataNode, "desc"); // Get top-level desc if present
  metadata.time = safeGetTextContent(metadataNode, "time");

  // Link
  const linkNode = metadataNode.getElementsByTagName("link")[0];
  if (linkNode) {
    metadata.link = {
      href: safeGetAttribute(linkNode, "href"),
      text: safeGetTextContent(linkNode, "text"),
    };
  }

  // Author
  const authorNode = metadataNode.getElementsByTagName("author")[0];
  if (authorNode) {
    const emailNode = authorNode.getElementsByTagName("email")[0];
    metadata.author = {
      name: safeGetTextContent(authorNode, "name"),
      email: emailNode
        ? `${safeGetAttribute(emailNode, "id")}@${safeGetAttribute(
            emailNode,
            "domain"
          )}`
        : null,
      link: null, // Placeholder if author link exists
    };
    const authorLinkNode = authorNode.getElementsByTagName("link")[0];
    if (authorLinkNode) {
      metadata.author.link = safeGetAttribute(authorLinkNode, "href");
    }
  }

  // Bounds within Metadata
  const boundsNode = metadataNode.getElementsByTagName("bounds")[0];
  if (boundsNode) {
    metadata.bounds = {
      // Store within metadata object as well
      minLat: safeParseFloat(safeGetAttribute(boundsNode, "minlat")),
      minLon: safeParseFloat(safeGetAttribute(boundsNode, "minlon")),
      maxLat: safeParseFloat(safeGetAttribute(boundsNode, "maxlat")),
      maxLon: safeParseFloat(safeGetAttribute(boundsNode, "maxlon")),
    };
  }

  return metadata;
};

const getBounds = (xmlDoc) => {
  // Only check root if not found in metadata previously
  const boundsNode = xmlDoc.documentElement?.getElementsByTagName("bounds")[0]; // Check direct child of root <gpx>
  if (boundsNode) {
    return {
      minLat: safeParseFloat(safeGetAttribute(boundsNode, "minlat")),
      minLon: safeParseFloat(safeGetAttribute(boundsNode, "minlon")),
      maxLat: safeParseFloat(safeGetAttribute(boundsNode, "maxlat")),
      maxLon: safeParseFloat(safeGetAttribute(boundsNode, "maxlon")),
    };
  }
  return null;
};

const getWaypoints = (xmlDoc) => {
  const waypoints = [];
  const wptNodes = xmlDoc.getElementsByTagName("wpt"); // Non-NS

  for (let i = 0; i < wptNodes.length; i++) {
    const wptNode = wptNodes[i];
    const lat = safeParseFloat(safeGetAttribute(wptNode, "lat"));
    const lng = safeParseFloat(safeGetAttribute(wptNode, "lon"));

    if (lat !== null && lng !== null) {
      const eleNode = wptNode.getElementsByTagName("ele")[0];
      const timeNode = wptNode.getElementsByTagName("time")[0];
      waypoints.push({
        lat,
        lng,
        ele: eleNode ? safeParseFloat(eleNode.textContent) : null,
        time: timeNode ? timeNode.textContent : null,
        name: safeGetTextContent(wptNode, "name"),
        cmt: safeGetTextContent(wptNode, "cmt"), // Get comment
        desc: safeGetTextContent(wptNode, "desc"),
        sym: safeGetTextContent(wptNode, "sym"),
        type: safeGetTextContent(wptNode, "type"),
      });
    } else {
      console.warn(
        "Skipping waypoint due to invalid lat/lon:",
        wptNode.outerHTML?.substring(0, 100)
      );
    }
  }
  return waypoints;
};

const getTracks = (xmlDoc) => {
  const tracks = [];
  const trackNodes = xmlDoc.getElementsByTagName("trk"); // Non-NS

  for (let i = 0; i < trackNodes.length; i++) {
    const trackNode = trackNodes[i];
    const trackInfo = {
      name: safeGetTextContent(trackNode, "name"),
      cmt: safeGetTextContent(trackNode, "cmt"),
      desc: safeGetTextContent(trackNode, "desc"),
      src: safeGetTextContent(trackNode, "src"),
      number: safeParseInt(safeGetTextContent(trackNode, "number")),
      type: safeGetTextContent(trackNode, "type"),
    };

    const trackData = getTrackPoints(trackNode);

    if (trackData.length > 0) {
      tracks.push({ trackInfo, trackData });
    } else {
      console.warn(
        "Skipping track because no track points were parsed:",
        trackInfo.name || `Track ${i}`
      );
    }
  }
  return tracks;
};

const EXTENSION_NAMESPACES = {
  garmin_tpx: GARMIN_TPX_NS,
  // Add other NS constants here if needed
};

const parsePoint = (pointNode) => {
  const lat = safeParseFloat(safeGetAttribute(pointNode, "lat"));
  const lng = safeParseFloat(safeGetAttribute(pointNode, "lon"));

  if (lat !== null && lng !== null) {
    const eleNode = pointNode.querySelector("ele"); // Non-NS
    const timeNode = pointNode.querySelector("time"); // Non-NS
    const extensionsNode = pointNode.querySelector("extensions"); // Non-NS

    const ele = eleNode ? safeParseFloat(eleNode.textContent) : null;
    const time = timeNode ? timeNode.textContent : null;

    let heartRate = null;
    let temperature = null;
    let cadence = null;

    if (extensionsNode) {
      for (const providerKey in EXTENSION_NAMESPACES) {
        const extNs = EXTENSION_NAMESPACES[providerKey];
        const hrNode = extensionsNode.getElementsByTagNameNS(extNs, "hr")[0];
        if (hrNode && heartRate === null) {
          // Take first found HR
          heartRate = safeParseInt(hrNode.textContent);
        }
        const atempNode = extensionsNode.getElementsByTagNameNS(
          extNs,
          "atemp"
        )[0];
        if (atempNode && temperature === null) {
          // Take first found Temp
          temperature = safeParseFloat(atempNode.textContent);
        }
        const cadNode = extensionsNode.getElementsByTagNameNS(extNs, "cad")[0];
        if (cadNode && cadence === null) {
          // Take first found Cadence
          cadence = safeParseInt(cadNode.textContent);
        }
      }
    }

    return {
      lat,
      lng,
      ele,
      time,
      heartrate: heartRate,
      temperature,
      cadence,
    };
  } else {
    console.warn(
      "Skipping point due to invalid lat/lon:",
      pointNode.outerHTML?.substring(0, 100)
    );
    return null;
  }
};

const getTrackPoints = (trackNode) => {
  const trackData = [];
  const trackSegments = trackNode.querySelectorAll("trkseg"); // Non-NS (Worked before)

  trackSegments.forEach((trackSegment) => {
    const trackPoints = trackSegment.querySelectorAll("trkpt"); // Non-NS (Worked before)

    trackPoints.forEach((trackPoint) => {
      const pointData = parsePoint(trackPoint);
      if (pointData) {
        trackData.push(pointData);
      }
    });
  });
  return trackData;
};

const getRoutes = (xmlDoc) => {
  const routes = [];
  const routeNodes = xmlDoc.getElementsByTagName("rte"); // Non-NS

  for (let i = 0; i < routeNodes.length; i++) {
    const routeNode = routeNodes[i];
    const routeInfo = {
      name: safeGetTextContent(routeNode, "name"),
      cmt: safeGetTextContent(routeNode, "cmt"),
      desc: safeGetTextContent(routeNode, "desc"),
      src: safeGetTextContent(routeNode, "src"),
      number: safeParseInt(safeGetTextContent(routeNode, "number")),
      type: safeGetTextContent(routeNode, "type"),
    };

    const routeData = getRoutePoints(routeNode);

    if (routeData.length > 0) {
      routes.push({ routeInfo, routeData });
    } else {
      console.warn(
        "Skipping route because no route points were parsed:",
        routeInfo.name || `Route ${i}`
      );
    }
  }
  return routes;
};

const getRoutePoints = (routeNode) => {
  const routeData = [];
  const routePoints = routeNode.querySelectorAll("rtept"); // Non-NS

  routePoints.forEach((routePoint) => {
    const pointData = parsePoint(routePoint);
    if (pointData) {
      routeData.push(pointData);
    }
  });
  return routeData;
};
